import React, { useState, useEffect } from 'react';
import { AlertTriangle, Calendar, FileText, RefreshCw, Filter, Bell } from 'lucide-react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import Button from '../../components/ui/Button';
import documentExpirationService from '../../services/documentExpirationService';
import documentManagementService from '../../services/documentManagementService';
import ExpirationStatisticsCard from './components/ExpirationStatisticsCard';
import ExpiringDocumentsTable from './components/ExpiringDocumentsTable';
import DepartmentExpirationChart from './components/DepartmentExpirationChart';
import ExpirationFilterPanel from './components/ExpirationFilterPanel';

const DocumentExpirationMonitor = () => {
  // State
  const [expiringDocuments, setExpiringDocuments] = useState([]);
  const [expiredDocuments, setExpiredDocuments] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [emailProcessing, setEmailProcessing] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState('disconnected');

  // Filters
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDocumentType, setSelectedDocumentType] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Global filter handler (required by GlobalFilterBar)
  const handleGlobalFilterChange = (filters) => {
    // Handle global filter changes if needed
    console.log('Global filters changed:', filters);
  };

  // Status categories
  const statusCategories = [
    { id: 'all', label: 'All Documents', icon: 'FileText', color: 'gray' },
    { id: 'expired', label: 'Expired', icon: 'AlertTriangle', color: 'red' },
    { id: 'critical', label: 'Critical (≤7 days)', icon: 'AlertTriangle', color: 'orange' },
    { id: 'warning', label: 'Warning (≤14 days)', icon: 'AlertTriangle', color: 'yellow' },
    { id: 'upcoming', label: 'Upcoming (≤30 days)', icon: 'Calendar', color: 'blue' }
  ];

  useEffect(() => {
    loadData();
    
    // Subscribe to real-time document expiration changes
    const documentSubscription = documentExpirationService?.subscribeToDocumentExpirationChanges((payload) => {
      console.log('Real-time document change:', payload);
      setRealtimeStatus('connected');
      
      // Show notification for critical changes
      const document = payload?.new;
      if (document && ['critical', 'expired']?.includes(document?.expiration_status)) {
        setSuccess(`Real-time alert: Document ${document?.document_name} status changed to ${document?.expiration_status}`);
        setTimeout(() => setSuccess(''), 5000);
      }
      
      // Reload data to reflect changes
      loadData();
    });

    // Subscribe to notification queue for email processing
    const notificationSubscription = documentExpirationService?.subscribeToNotificationQueue((payload) => {
      console.log('Real-time notification queued:', payload);
      const notification = payload?.new;
      const metadata = notification?.metadata || {};
      
      if (metadata?.email_type === 'document_expiration_alert') {
        setSuccess(`Email notification sent to ${metadata?.employee_name}`);
        setTimeout(() => setSuccess(''), 5000);
      }
    });

    // Set realtime status
    setRealtimeStatus('connected');

    return () => {
      // Cleanup subscriptions
      documentExpirationService?.unsubscribe(documentSubscription);
      documentExpirationService?.unsubscribe(notificationSubscription);
      setRealtimeStatus('disconnected');
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [selectedStatus, selectedDocumentType, selectedDepartment]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [expiringData, expiredData, statsData, deptData] = await Promise.all([
        documentExpirationService?.getExpiringDocuments({ maxDaysUntilExpiry: 30 }),
        documentExpirationService?.getExpiredDocuments(),
        documentExpirationService?.getExpirationStatistics(),
        documentExpirationService?.getExpirationsByDepartment()
      ]);

      setExpiringDocuments(expiringData || []);
      setExpiredDocuments(expiredData || []);
      setStatistics(statsData);
      setDepartmentStats(deptData || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Failed to load document expiration data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    try {
      const filters = {};

      if (selectedStatus !== 'all') {
        filters.expirationStatus = selectedStatus;
      }

      if (selectedDocumentType !== 'all') {
        filters.documentType = selectedDocumentType;
      }

      if (selectedDepartment !== 'all') {
        filters.department = selectedDepartment;
      }

      const data = await documentExpirationService?.getExpiringDocuments({
        ...filters,
        maxDaysUntilExpiry: selectedStatus === 'expired' ? undefined : 30
      });

      setExpiringDocuments(data || []);
    } catch (err) {
      console.error('Error applying filters:', err);
    }
  };

  const handleTriggerCheck = async () => {
    try {
      setRefreshing(true);
      setError('');
      setSuccess('');

      await documentExpirationService?.triggerExpirationCheck();
      
      setSuccess('Expiration check completed successfully. Alerts and notifications have been sent.');
      await loadData();
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error triggering expiration check:', err);
      setError(err?.message || 'Failed to trigger expiration check');
      setTimeout(() => setError(''), 5000);
    } finally {
      setRefreshing(false);
    }
  };

  const handleManualCheck = async () => {
    await handleTriggerCheck();
  };

  const handleProcessEmailQueue = async () => {
    try {
      setEmailProcessing(true);
      setError('');
      setSuccess('');

      const result = await documentExpirationService?.processEmailNotificationQueue();

      if (result?.success) {
        setSuccess(`Successfully processed ${result?.processed} email notifications`);
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      console.error('Error processing email queue:', err);
      setError(err?.message || 'Failed to process email notifications');
    } finally {
      setEmailProcessing(false);
    }
  };

  const handleSendTestEmail = async (documentId) => {
    try {
      setError('');
      setSuccess('');

      const result = await documentExpirationService?.sendDocumentExpirationEmail(documentId);

      if (result?.success) {
        setSuccess('Test email notification sent successfully');
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      console.error('Error sending test email:', err);
      setError(err?.message || 'Failed to send test email');
    }
  };

  const handleDownloadDocument = async (document) => {
    try {
      await documentManagementService?.downloadDocument(document?.storagePath, document?.documentName);
      setSuccess('Document downloaded successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to download document');
      setTimeout(() => setError(''), 3000);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      expired: 'bg-red-100 text-red-800 border-red-200',
      critical: 'bg-orange-100 text-orange-800 border-orange-200',
      warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      upcoming: 'bg-blue-100 text-blue-800 border-blue-200',
      valid: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <GlobalFilterBar onFilterChange={handleGlobalFilterChange} />
        <DataRefreshIndicator />
        <LegalDisclaimerBanner />
        <ComplianceAlertBanner />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading document expiration data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <GlobalFilterBar onFilterChange={handleGlobalFilterChange} />
      <DataRefreshIndicator />
      <LegalDisclaimerBanner />
      <ComplianceAlertBanner />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <AlertTriangle className="h-8 w-8 text-orange-500" />
                Document Expiration Monitor
              </h1>
              <p className="mt-2 text-gray-600">
                Automated renewal reminders for work permits, visas, driver licenses, and passports
              </p>
              <div className="mt-2 flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${
                    realtimeStatus === 'connected' ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                  }`} />
                  <span className="text-sm text-gray-600">
                    Real-time notifications: {realtimeStatus === 'connected' ? 'Active' : 'Disconnected'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-blue-500" />
                  <span className="text-sm text-gray-600">
                    Instant email alerts enabled
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleProcessEmailQueue}
                disabled={emailProcessing}
                variant="secondary"
                className="flex items-center gap-2"
              >
                <Bell className="h-4 w-4" />
                {emailProcessing ? 'Processing...' : 'Process Email Queue'}
              </Button>
              <Button
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                variant="secondary"
                className="flex items-center gap-2"
              >
                <Filter className="h-4 w-4" />
                Filters
              </Button>
              <Button
                onClick={handleManualCheck}
                disabled={refreshing}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Checking...' : 'Manual Check'}
              </Button>
            </div>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <p className="text-amber-800">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-green-600" />
              <p className="text-green-800">{success}</p>
            </div>
          </div>
        )}

        {/* Filter Panel */}
        {showFilterPanel && (
          <ExpirationFilterPanel
            selectedStatus={selectedStatus}
            selectedDocumentType={selectedDocumentType}
            selectedDepartment={selectedDepartment}
            onStatusChange={setSelectedStatus}
            onDocumentTypeChange={setSelectedDocumentType}
            onDepartmentChange={setSelectedDepartment}
            onClose={() => setShowFilterPanel(false)}
          />
        )}

        {/* Status Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {statusCategories?.map(category => (
            <button
              key={category?.id}
              onClick={() => setSelectedStatus(category?.id)}
              className={`p-4 rounded-lg border-2 transition-all ${
                selectedStatus === category?.id
                  ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl font-bold text-gray-900">
                  {category?.id === 'all'
                    ? statistics?.total || 0
                    : statistics?.[category?.id] || 0}
                </span>
                <span className={`p-2 rounded-lg ${
                  category?.color === 'red' ? 'bg-red-100 text-red-600' :
                  category?.color === 'orange' ? 'bg-orange-100 text-orange-600' :
                  category?.color === 'yellow' ? 'bg-yellow-100 text-yellow-600' :
                  category?.color === 'blue' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {category?.icon === 'AlertTriangle' && <AlertTriangle className="h-5 w-5" />}
                  {category?.icon === 'Calendar' && <Calendar className="h-5 w-5" />}
                  {category?.icon === 'FileText' && <FileText className="h-5 w-5" />}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-600">{category?.label}</p>
            </button>
          ))}
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <ExpirationStatisticsCard
            title="By Document Type"
            statistics={statistics?.byType}
          />
          <ExpirationStatisticsCard
            title="Critical Actions Required"
            statistics={{
              expired: statistics?.expired,
              critical: statistics?.critical,
              warning: statistics?.warning
            }}
          />
          <ExpirationStatisticsCard
            title="Overall Status"
            statistics={{
              valid: statistics?.valid,
              upcoming: statistics?.upcoming,
              total: statistics?.total
            }}
          />
        </div>

        {/* Department Chart */}
        {departmentStats?.length > 0 && (
          <div className="mb-8">
            <DepartmentExpirationChart data={departmentStats} />
          </div>
        )}

        {/* Expiring Documents Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Expiring Documents</h2>
                <p className="mt-1 text-sm text-gray-600">
                  {expiringDocuments?.length} document(s) requiring attention
                </p>
              </div>
              <Bell className="h-6 w-6 text-orange-500" />
            </div>
          </div>
          <ExpiringDocumentsTable
            documents={expiringDocuments}
            onDownload={handleDownloadDocument}
            getStatusColor={getStatusColor}
          />
        </div>
      </main>
    </div>
  );
};

export default DocumentExpirationMonitor;